import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CardContent, Typography, CircularProgress, Alert, TextField, InputAdornment, FormControlLabel, Switch, Stack } from '@mui/material';
import { Search, ConfirmationNumber } from '@mui/icons-material';
import { useGetTicketsCreatedByQuery } from '../../../services/TicketService';
import { PageContainer, PageCard, SectionHeader, SectionPaper } from '../../common/components/styled-ui-elements';
import WorklistGrid from '../components/TicketList/WorklistGrid';
import { BackToDashboardButton, FloatingActionButton } from '../../common/components/navigation';

const TicketsCreatedBy: React.FC = () => {
  const [searchParams] = useSearchParams();
  const createdBy = searchParams.get('createdBy');
  const [searchText, setSearchText] = useState('');
  const [showClosedTickets, setShowClosedTickets] = useState(false);
  
  const { data: tickets, error, isLoading } = useGetTicketsCreatedByQuery(
    { customerName: createdBy || '' },
    { skip: !createdBy }
  );

  if (isLoading) {
    return (
      <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
        <Alert severity="error">
          Failed to load tickets. Please try again.
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ backgroundColor: '#f8fafc' }}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <SectionHeader 
                  icon={<ConfirmationNumber sx={{ mr: 1, color: '#3b82f6' }} />}
                  title="My Tickets"
                />
                <Typography 
                  variant="body2" 
                  sx={{ color: '#64748b', ml: 4 }}
                >
                  Tickets created by {createdBy}
                </Typography>
              </Box>
              <BackToDashboardButton />
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Search and Filter Section */}
            <SectionPaper sx={{ mb: 3 }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search tickets by title, description, category, status, or assignee..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#f9fafb',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                      },
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showClosedTickets}
                      onChange={(e) => setShowClosedTickets(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Show closed/resolved tickets
                    </Typography>
                  }
                />
              </Stack>
            </SectionPaper>
            
            <WorklistGrid 
              tickets={tickets || []} 
              searchText={searchText} 
              showClosedTickets={showClosedTickets}
            />
          </CardContent>
        </PageCard>
      </Box>
      
      {/* Floating Action Button */}
      <FloatingActionButton 
        label="Create Ticket" 
        navigateTo="/tickets/create" 
        ariaLabel="Create new ticket"
      />
    </PageContainer>
  );
};

export default TicketsCreatedBy;
